import { ArrowLeft, BoxArrowUpRight, Mailbox } from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';

import { Avatar, Button, Divider, Space, Typography } from 'antd';

import default_avatar from '@/assets/images/default_avatar.jpg';
import fallbackLessonBanner from '@/assets/images/empty-state-saved-lesson.png';
import EmptyState from '@/components/shared/EmptyState';
import Actions from '@/components/shared/Notifications/TabInvites/Item/Actions';
import { getCdnUrl } from '@/helpers';
import { useAppSelector } from '@/hooks/redux';
import { enNotificationNames, enNotificationOrigin } from '@/interfaces/notification';
import { setNotification } from '@/redux/notification/slice';
import styled, { css, StyledProp } from '@/styled';

const NotificationItem = (props: StyledProp) => {
  const notification = useAppSelector(store => store.notification.notification);
  const isInvite = notification?.origin == enNotificationOrigin.invite;
  const dispatch = useDispatch();

  const goBack = () => {
    dispatch(setNotification(null));
  };

  if (!notification) {
    return (
      <div className={props.className} data-empty='true'>
        <EmptyState
          icon={<Mailbox size={64} />}
          description='Clique nas notificações de novidades ou convites para visualizar a mensagem.'
          title='Nenhuma mensagem selecionada.'
          className='empty-state-container'
        />
      </div>
    );
  }

  const { course, hash } = notification;

  const makeAccessLink = () => {
    if (notification.type === enNotificationNames.news) {
      return `/curso/${course.courseHash}`;
    }

    return course.courseLink;
  };

  return (
    <div className={props.className} data-empty='false'>
      <div className='goBack-container'>
        <Button className='goBack' icon={<ArrowLeft size={18} />} onClick={goBack} />
      </div>
      <div className='update-container'>
        <div className='title-container' data-type={notification.type}>
          <Space direction='vertical'>
            {!isInvite && (
              <Typography.Paragraph ellipsis={{ rows: 4 }} className='title'>
                {notification.description}
              </Typography.Paragraph>
            )}

            <Typography.Paragraph ellipsis={{ rows: 3 }} className='course-name-container'>
              {!isInvite && <span className='course-name'>Curso: </span>} {course?.title}
            </Typography.Paragraph>
          </Space>
        </div>
        {notification.type !== enNotificationNames.news && (
          <div className='cover-container'>
            <img className='cover' src={getCdnUrl(course?.cover, fallbackLessonBanner)} alt='imagem da curso' />
          </div>
        )}
        <div className='avatar-container'>
          <Space direction='vertical'>
            <div className='avatar'>
              <Avatar src={getCdnUrl(course?.author?.avatar ?? course?.producer?.avatar, default_avatar)} size={82} />
              <Typography.Paragraph ellipsis={{ rows: 3 }}>
                {course?.author?.name ?? course?.producer?.name}
              </Typography.Paragraph>
            </div>
            {isInvite && <Actions hash={hash} />}
          </Space>
        </div>
        {notification.type === enNotificationNames.news && (
          <p className='message' dangerouslySetInnerHTML={{ __html: notification.message }} />
        )}
      </div>
      <div className='link-container'>
        <Divider />
        <Typography.Link className='link' underline href={makeAccessLink()}>
          Acessar
          <BoxArrowUpRight className='link-icon' size={13} />
        </Typography.Link>
      </div>
    </div>
  );
};

const NotificationItemStyled = styled(NotificationItem)(
  ({ theme }) => css`
    height: 460px;
    flex-grow: 1;
    background-color: ${theme.palette.gray[9]};
    overflow: auto;
    border: ${theme.palette.gray[10]};
    border-style: solid;
    border-width: 24px;

    .empty-state-container {
      padding: ${theme.space.pxToRem(24)};
    }

    ${theme.breakpoints.down('lg')} {
      background-color: ${theme.palette.gray[9]};
      position: relative;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      min-height: 100%;

      &[data-empty='false'] {
        position: absolute;
      }
    }

    .goBack-container {
      background-color: ${theme.palette.gray[10]};
      .goBack {
        height: 24px;
        border: none;
        display: none;
        background-color: ${theme.palette.gray[10]};

        ${theme.breakpoints.down('lg')} {
          display: block;
        }
      }
    }

    .update-container {
      ${theme.breakpoints.down('lg')} {
        display: block;
        border-top: 20px solid ${theme.palette.gray[10]};
      }

      padding: ${theme.space.pxToRem(24)};
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      height: auto;
      ${theme.breakpoints.down('lg')} {
        margin-top: 0;

        width: 100%;
        justify-content: center;
      }

      .title-container {
        width: 50%;

        ${theme.breakpoints.down('lg')} {
          width: 100%;
          height: auto;
        }
        &[data-type='news'] {
          width: 100%;
        }
      }

      .cover-container {
        width: 50%;

        ${theme.breakpoints.down('lg')} {
          width: 100%;
          height: auto;
        }
      }

      .avatar-container {
        width: 100%;
      }

      .avatar-container {
        margin-top: ${theme.space.pxToRem(16)};
        margin-bottom: ${theme.space.pxToRem(-16)};

        button {
          padding: ${theme.space.spacing(0, 2)};
          height: 24px;
          border: 1px solid ${theme.variables.borderColor};
          background: ${theme.palette.alpha(theme.palette.gray[0], 0.02)};

          svg {
            margin-right: ${theme.space.pxToRem(4)};
          }
        }
      }

      .link-container {
        width: 100%;
        margin-top: ${theme.space.pxToRem(24)};
        margin-bottom: ${theme.space.pxToRem(24)};
      }

      .cover-container {
        display: flex;
        justify-content: flex-end;

        > div {
          margin: auto;
        }
      }

      .title,
      .course-name-container,
      .course-name {
        font-size: 16px;
      }

      .course-name {
        color: ${theme.colorTextQuaternary};
      }

      .cover {
        width: 248px;
        max-width: 100%;
        height: 137px;
        border-radius: 4px;
        margin: auto;
      }

      .ant-divider {
        border-color: ${theme.colorTextQuaternary};
      }

      .message {
        padding-top: ${theme.space.spacing(8)};
        color: ${theme.colorText};
        word-break: break-all;
      }

      .avatar {
        display: flex;
        align-items: flex-start;
        gap: ${theme.space.spacing(2)};

        span.ant-typography {
          font-size: ${theme.space.pxToRem(14)};
          color: ${theme.colorTextQuaternary};
        }

        .ant-avatar {
          min-width: 24px;
          min-height: 24px;
          max-width: 24px;
          max-height: 24px;

          .ant-avatar-string {
            line-height: 24px;
          }
        }
      }
    }

    .link {
      display: flex;
      justify-content: flex-end;
      font-size: 16px;
      margin: ${theme.space.pxToRem(23)};

      .link-icon {
        margin-left: ${theme.space.pxToRem(8)};
      }
    }
  `
);

export default NotificationItemStyled;
